<template>
    <modal id="question-modal"
           size="large"
           v-model="showModal"
           :title="generateModalTitle('question')"
           @submit="submit"
           :loading="loading"
           :preloader="preloader"

    >
        <form :data-url='selectedUrl ? selectedUrl : apiUrl.QUESTION'
              method="POST"
              ref="form"
              enctype="multipart/form-data"
              @submit.prevent="submitData">
            <div class="row">
                <div class="col-md-6">
                    <app-form-group-selectable
                        type="search-select"
                        :label="$t('lesson')"
                        list-value-field="title"
                        :chooseLabel="$t('lesson')"
                        v-model="formData.lesson_id"
                        :required="true"
                        disabled
                        :error-message="$errorMessage(errors, 'lesson_id')"
                        :fetch-url="`${apiUrl.TENANT_SELECTABLE_LESSON}`"
                    />
                </div>
                <div class="col-md-6">
                    <app-form-group
                        :label="$t('questions.question_text')"
                        type="textarea"
                        v-model="formData.question_text"
                        :placeholder="$placeholder('questions.question_text')"
                        :required="true"
                        :error-message="$errorMessage(errors, 'question_text')"
                    />
                </div>
            </div>
            <div class="row">
                <div class="col-md-6">
                    <app-form-group
                        :label="$t('questions.question_type')"
                        type="radio"
                        :list="[
                            {id:'mcq',value: $t('MCQ')},
                            {id:'text',value: $t('Text')},
                        ]"
                        v-model="formData.question_type"
                        :placeholder="$placeholder('questions.question_type')"
                        :required="true"
                        :error-message="$errorMessage(errors, 'question_type')"
                        @change="handleQuestionTypeChange"
                    />
                </div>
            </div>

            <!-- Dynamic Fields -->
            <div class="col-md-12 mt-3">
                <!-- For MCQ -->
                <div v-if="formData.question_type === 'mcq'">
                    <div v-for="(option, index) in mcqOptions" :key="index" class="d-flex mb-2">
                        <input
                            type="text"
                            class="form-control"
                            v-model="option.text"
                            :placeholder="'Option ' + (index + 1)"
                            :class="{ 'is-invalid': errors[`mcq_options.${index}`] }"
                        />
                        <button type="button" class="btn btn-danger ml-2" @click="removeMcqOption(index)" v-if="mcqOptions.length > 1">-</button>

                        <!-- Correct Answer Checkbox (acts like a radio) -->
                        <div class="form-check ml-2">
                            <input
                                class="form-check-input"
                                type="radio"
                                :checked="option.is_correct"
                                @change="setCorrectAnswer(index)"
                                name="is_correct"
                            />
                            <label class="form-check-label">Correct</label>
                        </div>

                    </div>

                    <!-- Add Option -->
                    <button type="button" class="btn btn-primary" @click="addMcqOption">+ Add Option</button>

                    <!-- Error if no correct answer is selected -->
                    <div v-if="correctAnswerError" class="text-danger mt-2">
                        Please select one correct answer.
                    </div>
                </div>

                <!-- For Open Ended -->
                <div v-if="formData.question_type === 'text'">
                    <textarea
                        class="form-control"
                        v-model="openEndedPlaceholder"
                        placeholder="Enter the answer text."
                    ></textarea>
                </div>
            </div>

            <div class="form-group mb-0">
                <label>
                    {{ $t('attachments') }}
                </label>
                <app-input
                    type="dropzone"
                    v-model="attachments"
                    :error-message="errorMessageInArray(errors, 'attachments.0')"
                />
                <small class="text-muted">{{ $t('leave_attachment_allowed_file_types') }}</small>
            </div>
        </form>
    </modal>
</template>

<script>
import FormHelperMixins from "../../../../../common/Mixin/Global/FormHelperMixins";
import ModalMixin from "../../../../../common/Mixin/Global/ModalMixin";
import {
    errorMessageInArray,
    formDataAssigner
} from "../../../../../common/Helper/Support/FormHelper";
import {axiosGet, axiosPost} from "../../../../../common/Helper/AxiosHelper";

export default {
    name: "QuestionCreateEdit",
    props: {
        selectedUrl: {
            type: String,
            default: null
        }
    },
    components: {},
    mixins: [FormHelperMixins, ModalMixin],
    comments: {},
    data() {
        return {
            errorMessageInArray,
            formData: {
                lesson_id: '',
                question_text: '',
                question_type: '',
            },
            mcqOptions: [
                {text: '', is_correct: false}
            ],
            openEndedPlaceholder: '',
            attachments: [],
            errors: {},
            correctAnswerError: false
        };
    },
    watch: {
        selectedUrl: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.fetchQuestion();
                    this.$nextTick(() => {
                        // Ensure DOM updates after data is set
                    });
                }
            }
        }
    },
    created() {
        const segments = window.location.pathname.split('/');
        const lessonId = segments.includes('lesson') ? segments[segments.indexOf('lesson') + 1] : null;

        if (lessonId && !this.selectedUrl) {
            this.formData.lesson_id = lessonId;
        }
    },
    methods: {
        addMcqOption() {
            this.mcqOptions.push({text: ''});
        },
        removeMcqOption(index) {
            this.mcqOptions.splice(index, 1);
        },
        setCorrectAnswer(index) {
            this.mcqOptions.forEach((option, i) => {
                option.is_correct = i === index;
            });
        },
        fetchQuestion() {
            if (this.selectedUrl) {
                axiosGet(this.selectedUrl)
                    .then(({data}) => {
                        console.log('Question API response:', data.answers);
                        this.formData.question_type = data.question_type;

                        if (data.question_type === 'mcq' && Array.isArray(data.answers)) {
                            this.mcqOptions = data.answers.map(option => ({
                                text: option.answer_text,
                                is_correct: !!option.is_correct
                            }));
                            console.log('MCQ Options set:', this.mcqOptions);
                        } else if (data.question_type === 'text') {
                            this.openEndedPlaceholder = data.answers?.[0]?.answer_text || '';
                            console.log("One Line Ans : ", this.openEndedPlaceholder)
                        }
                    })
                    .catch(error => {
                        console.error('Failed to fetch question:', error);
                    });
            }
        },
        handleQuestionTypeChange() {
            // Optionally reset options when question_type changes
            if (this.formData.question_type === 'mcq') {
                this.mcqOptions = [];
            } else if (this.formData.question_type === 'text') {
                this.openEndedPlaceholder = '';
            }
        },
        submit() {
            this.loading = true;
            let formData = {...this.formData}
            formData = formDataAssigner(new FormData, formData);
            let url = this.apiUrl.QUESTION;
            // Remove unwanted fields
            this.attachments.forEach(file => {
                formData.append('attachments[]', file)
            })
            if (this.selectedUrl) {
                formData.append('_method', "PUT");
                url = this.selectedUrl;
            }
            if (this.formData.question_type === 'mcq') {
                this.mcqOptions.forEach((option, index) => {
                    formData.append(`mcq_options[${index}][text]`, option.text);
                    formData.append(`mcq_options[${index}][is_correct]`, option.is_correct ? 1 : 0);
                });
            }

            axiosPost(url, formData).then(({data}) => {
                this.loading = false;
                $('#question-modal').modal('hide');
                $('.modal-backdrop').remove();
                this.showModal = false;
                this.toastAndReload(data.message, 'question-table');
            }).catch(({response}) => {
                this.loading = false;
                this.message = '';
                this.errors = response.data.errors || {};
                this.fieldStatus.isSubmit = true;
                if (response.status !== 422) {
                    this.$toastr.e(response.data.message || response.statusText);
                }
            });

        },
        afterSuccess({data}) {
            this.formData = {};
            $('#question-modal').modal('hide');
            this.toastAndReload(data.message, 'question-table')
            this.$emit('input', false);
        },
        afterError(response) {
            this.loading = false;
            this.errors = response.data.errors;
        },
    }
}
</script>
